/* eslint-disable no-param-reassign */
import { type EChartsExtensionInstallRegisters } from 'echarts/types/src/extension';
import type { Payload } from 'echarts/types/src/util/types';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import { type dvRadarSeriesModelClazz as dvRadarSeriesModel } from './radarSeriesModel';
import { Line } from 'echarts/lib/util/graphic';
import { ViewRootGroup } from 'echarts/lib/util/types';
import * as graphic from 'echarts/lib/util/graphic';
import { type dvRadarSymbol } from './radarView';
import { ElementEvent } from 'echarts';

const transparentColor = 'rgba(0,0,0,0)';

type ActionPayload = Payload & {
  seriesIndex?: number | number[];
  seriesId?: string | string[];
  seriesName?: string | string[];
};

export function installRadarAction(registers: EChartsExtensionInstallRegisters) {
  type MouseOverSectorPayload = ActionPayload & {
    event: ElementEvent;
    line: Line;
    group: ViewRootGroup;
  };

  // 记录不同series的颜色
  const colorList = [];

  registers.registerAction(
    { type: 'radarMouseOverSector', update: 'none' },
    (payload: MouseOverSectorPayload, ecModel: GlobalModel, api: ExtensionAPI) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvRadar',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as dvRadarSeriesModel[];

      result.forEach(seriesModel => {
        const sectorSelector = seriesModel.option.sectorSelector;
        if (!sectorSelector) {
          return;
        } else if (sectorSelector.show === undefined) {
          sectorSelector.show = true;
        }
        if (sectorSelector.show === true) {
          if (sectorSelector.sectorStyle) {
            payload.event.target.setStyle(sectorSelector.sectorStyle); // 将扇形的填充颜色修改为透明灰色
          }

          const x = seriesModel.coordinateSystem.cx;
          const y = seriesModel.coordinateSystem.cy;
          const r = seriesModel.coordinateSystem.r;
          // 确定轴的数量
          const dimensionNumber = seriesModel.coordinateSystem.dimensions.length;
          // 计算每个扇形的角度
          const angleRange = (Math.PI * 2) / dimensionNumber;

          if (sectorSelector.lineStyle) {
            // 计算外侧弧线中点的坐标
            let midAngle = payload.event.target.hover_id * angleRange - Math.PI / 2;
            let midX = x + (r + sectorSelector.pointerOffset) * Math.cos(midAngle);
            let midY = y + (r + sectorSelector.pointerOffset) * Math.sin(midAngle);
            payload.line.attr({ shape: { x1: x, y1: y, x2: midX, y2: midY } });
          }

          payload.group.eachChild(function (symbolGroup: graphic.Group, group_index: number) {
            symbolGroup.eachChild(function (symbolPath: dvRadarSymbol) {
              if (symbolPath.type === 'polygon') {
                colorList[group_index] = symbolPath.style.fill;
              }
              if (symbolPath.type === 'group') {
                setStyleForSymbol(symbolPath, colorList, group_index, payload, sectorSelector);
              }
            });
          });
        }
      });
    }
  );

  registers.registerAction(
    { type: 'radarMouseOutSector', update: 'none' },
    (payload: MouseOverSectorPayload, ecModel: GlobalModel, api: ExtensionAPI) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvRadar',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as dvRadarSeriesModel[];

      result.forEach(seriesModel => {
        let target = payload.event.target; // 获取被移出的扇形对象，恢复透明色
        if (payload.event.topTarget.type != 'line') {
          target.setStyle('fill', transparentColor);
          payload.line.attr({
            shape: {
              x1: seriesModel.coordinateSystem.cx,
              y1: seriesModel.coordinateSystem.cy,
              x2: seriesModel.coordinateSystem.cx,
              y2: seriesModel.coordinateSystem.cy
            }
          });
          payload.group.eachChild(function (symbolGroup: graphic.Group, group_index: number) {
            clearStyleForSymbol(symbolGroup, colorList, group_index);
          });
        }
      });
    }
  );
}

function setStyleForSymbol(
  symbolPath: dvRadarSymbol,
  colorList: string[],
  group_index: number,
  payload,
  sectorSelector
) {
  symbolPath.eachChild(function (dot: dvRadarSymbol, index: number) {
    if (index === payload.event.target.hover_id) {
      if (sectorSelector.symbolStyle) {
        dot.attr({ style: sectorSelector.symbolStyle });
      } else {
        dot.attr({ style: { fill: 'white', stroke: colorList[group_index] } });
      }
    }
  });
}

function clearStyleForSymbol(symbolGroup: graphic.Group, colorList: string[], group_index: number) {
  symbolGroup.eachChild(function (symbolPath: dvRadarSymbol) {
    if (symbolPath.type === 'group') {
      symbolPath.eachChild(function (dot: dvRadarSymbol) {
        dot.attr({ style: { fill: colorList[group_index], stroke: null } });
      });
    }
  });
}
